import { pipe, Option, Array as A } from 'effect';

import type { PaymentInfoData, PaymentInvoiceData, PaymentMetaOption } from '@/domain/payments/types';

/** More invoice numbers than this collapse to "N invoices". */
const MAX_LISTED_INVOICES = 3;

export type ConfirmPayInfo = Pick<
  PaymentInfoData,
  'vendorName' | 'currencyCode' | 'withholdingTax' | 'payFrom' | 'payTo' | 'paymentDate' | 'transferReason'
>;

export type ConfirmPayInvoice = Pick<
  PaymentInvoiceData,
  'referenceNumber' | 'amount' | 'amountToPay' | 'amountToPayAfterTax'
>;

export interface ConfirmPaySummary {
  vendorName: string;
  /** Reference numbers of the invoices that have one. */
  invoiceNumbers: ReadonlyArray<string>;
  /** All the invoices, with or without a reference number. */
  invoiceCount: number;
  /** True when there are too many invoices to list — show their count instead. */
  isInvoiceListCollapsed: boolean;
  currencyCode: string;
  invoiceAmount: number;
  withholdingTaxPercent: number;
  hasWithholdingTax: boolean;
  /** The withheld amount, positive; shown with a minus. */
  withholdingTaxAmount: number;
  paymentAmount: number;
  payFromLabel: string | null;
  /** The beneficiary nickname — it already carries the masked account, e.g. "ILS **** 0001". */
  payToLabel: string | null;
  paymentDate: string;
  transferReason: string;
}

// Amounts arrive as decimal strings; summing in cents keeps 0.1 + 0.2 from drifting.
const toCents = (value: string | null | undefined): number =>
  pipe(
    Option.fromNullable(value),
    Option.map((v) => parseFloat(v)),
    Option.filter(Number.isFinite),
    Option.map((n) => Math.round(n * 100)),
    Option.getOrElse(() => 0),
  );

const sumCents = (invoices: ReadonlyArray<ConfirmPayInvoice>, pick: (invoice: ConfirmPayInvoice) => number): number =>
  A.reduce(invoices, 0, (acc, invoice) => acc + pick(invoice));

const fromCents = (cents: number): number => cents / 100;

/**
 * Everything the Confirm & Pay dialog shows, from the payment as it is in the store.
 * A merged payment covers several invoices: their amounts are summed.
 */
export const buildConfirmPaySummary = (
  info: ConfirmPayInfo,
  invoices: ReadonlyArray<ConfirmPayInvoice>,
  payFromOptions: ReadonlyArray<PaymentMetaOption> = [],
): ConfirmPaySummary => {
  const amountToPayCents = sumCents(invoices, (i) => toCents(i.amountToPay));
  const paymentCents = sumCents(invoices, (i) => toCents(i.amountToPayAfterTax));

  const invoiceNumbers = pipe(
    invoices,
    A.map((i) => i.referenceNumber),
    A.filter((ref) => Boolean(ref)),
  );

  const withholdingTaxPercent = info.withholdingTax ?? 0;

  return {
    vendorName: info.vendorName,
    invoiceNumbers,
    invoiceCount: invoices.length,
    isInvoiceListCollapsed: invoices.length > MAX_LISTED_INVOICES,
    currencyCode: info.currencyCode,
    invoiceAmount: fromCents(sumCents(invoices, (i) => toCents(i.amount))),
    withholdingTaxPercent,
    hasWithholdingTax: withholdingTaxPercent !== 0,
    withholdingTaxAmount: fromCents(amountToPayCents - paymentCents),
    paymentAmount: fromCents(paymentCents),
    payFromLabel: pipe(
      A.findFirst(payFromOptions, (option) => option.value === info.payFrom),
      Option.map((option) => option.label),
      Option.getOrNull,
    ),
    payToLabel: pipe(
      Option.fromNullable(info.payTo),
      Option.map(({ nickname }) => nickname),
      Option.getOrNull,
    ),
    paymentDate: info.paymentDate,
    transferReason: info.transferReason ?? '',
  };
};
